import { colorFields, collectionLayoutField } from "./_shared";
/**
 * Collections section — horizontal scrollable strip of category tiles.
 * Renders only when the store has 2+ categories. Settings are minimal because
 * the content is data-driven (categories) rather than authored copy.
 */
export const collectionsSchema = {
    type: "collections",
    name: { ar: "تصفح الفئات", en: "Shop by collection" },
    description: {
        ar: "شريط أفقي بكروت الفئات. بيظهر تلقائيًا لو عندك فئتين أو أكتر.",
        en: "Horizontal strip of category tiles. Appears when you have 2+ categories.",
    },
    // RAISED 1 → 3 (2026-07-20, `nature` family). The cap existed to stop a
    // seller stacking redundant copies of one category grid, and with a single
    // layout that was the only thing a second instance could be. It is no longer
    // true: `nature` ships THREE distinct category presentations on one page —
    // a heading rail near the top, a marquee ticker mid-page, and a promo-card
    // carousel below it — and they do different jobs. Capping at 1 would have
    // made that family unreproducible, which is exactly the signal this
    // workstream exists to catch. No published store moves: raising a maximum
    // cannot invalidate an existing template.
    maxPerTemplate: 3,
    availableOnTemplates: ["home"],
    settings: [
        collectionLayoutField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "عنوان القسم", en: "Section title" },
            validate: { max: 60 },
            default: { ar: "تصفح حسب الفئة", en: "Shop by category" },
        },
        // The kicker above the title. `BuyerCollectionGrid` has always RENDERED an
        // eyebrow (the `caption-tile` variant even prints it on every tile), but
        // the schema never declared the field — so it was unreachable from the
        // inspector and only settable by hand-editing the JSON. Declared here
        // because avyro's categories block leads with "best seller collections"
        // above "Shop by Category", and that kicker is part of the layout's
        // identity, not decoration.
        //
        // Note `description` is in the same position (rendered by the `rail`
        // variant, undeclared). Left alone: no family built so far needs it, and
        // guessing at its copy limits would be inventing a control.
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "نص صغير فوق العنوان (اختياري)", en: "Eyebrow (optional)" },
            helpText: {
                ar: 'نص صغير فوق عنوان القسم، مثل "أفضل المجموعات".',
                en: 'A small label above the section title, e.g. "Best seller collections".',
            },
            validate: { max: 40 },
        },
        {
            /**
             * Whether the section prints its own heading.
             *
             * Added 2026-07-20 for the `hype` family, whose `ab-figma-categories`
             * rail ships its `.ab-categories-title` element EMPTY — the tiles' own
             * kicker + label are the whole of its copy. There was no way to express
             * that: `title` falls back to a built-in "Shop by Collection" whenever
             * it resolves empty, so a theme could not turn the heading off.
             *
             * Deliberately the SAME field id and semantics `category-sections`
             * already uses, rather than a new vocabulary for one concept. Defaults
             * to `true`, which is what every existing store already renders, so
             * applying this field to a published theme cannot move it.
             */
            id: "showSectionTitle",
            type: "checkbox",
            label: { ar: "إظهار عنوان القسم", en: "Show section title" },
            helpText: {
                ar: "اقفلها لو الكروت نفسها كفاية من غير عنوان فوقها.",
                en: "Turn off when the tiles speak for themselves and need no heading.",
            },
            default: true,
        },
        {
            id: "count",
            type: "range",
            label: { ar: "عدد الفئات المعروضة", en: "Categories shown" },
            helpText: {
                ar: "عدد كروت الفئات اللي هتظهر. 1 = صف واحد ممتد، 2 = نصين، 3 = تلاتة، 4 = شبكة 2×2.",
                en: "How many category tiles to show. 1 = full width, 2 = halves, 3 = thirds, 4 = 2×2 grid.",
            },
            min: 1,
            max: 4,
            step: 1,
            default: 3,
            // A6: the compact strip + chips variants render the FULL category list, so
            // the 1–4 cap does nothing there. `bento` is the same — its tall/short
            // rhythm only reads as a mosaic with the whole list, and so is `rail`
            // (a carousel that scrolls the full set).
            showIf: {
                variant: [
                    "standard",
                    "overlay-pill",
                    "slider",
                    "list",
                    "masonry",
                    "featured",
                ],
            },
        },
        {
            /**
             * How the BENTO mosaic collapses on small screens.
             *
             * A SETTING rather than a second variant, and the evidence is that their
             * own two `ab-categories-section` families disagree while sharing every
             * other value. MEASURED 2026-07-20 at 390px:
             *
             *   elite-furniture — `display:grid`, one 354px column, section 1618px
             *   sharp-bags      — `display:flex`, snap scroller, section 331px
             *
             * and at 1440 both are the identical 54.5% / 45.5% mosaic with the same
             * 20px gap, the same `rgba(0,0,0,.2)` dim and the same 28px/w500/ls .56px
             * uppercase white tile label. Two variants for one layout that differs
             * only in how it folds is the redundancy that got 7 product cards
             * deleted; this is the same call the plan doc makes for logo-on/off and
             * newsletter-on/off ("seller toggles, not variants").
             *
             * Defaults to `stack`, which is what every existing store already
             * renders — applying this field to a published theme cannot move it.
             */
            id: "mobileLayout",
            type: "select",
            label: { ar: "العرض على الموبايل", en: "On small screens" },
            helpText: {
                ar: "تحت 1024 بكسل: البلاطات تحت بعضها، ولا صف بيتمرر جنبًا.",
                en: "Below 1024px: stack the tiles, or scroll them sideways in one row.",
            },
            options: [
                { value: "stack", label: { ar: "تحت بعضها", en: "Stacked" } },
                { value: "scroll", label: { ar: "صف بيتمرر", en: "Side scroller" } },
            ],
            default: "stack",
            showIf: { variant: ["bento"] },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=collections.js.map